<script setup>
import { reactive, onMounted, provide, onBeforeUnmount, watch } from "vue";
import { useI18n } from "vue-i18n";
import { registerEvents } from "@/composables/components";
import * as UMS$Feedback from "@/apis/ums/feedback";

import FeedbackFormModal from "./modals/FeedbackFormModal.vue";
import FeedbackFormSuccess from "./modals/FeedbackFormSuccess.vue";
import QButton from "@/components/ui/QButton.vue";

import fallbackLayoutEn from "./layouts/qmod.json";
import fallbackLayoutFr from "./layouts/qmod-fr.json";

const props = defineProps({
    /**
     * Enable or disable the integrated feedback button
     * @values true, false
     */
    buttonEnabled: {
        type: Boolean,
        default: true,
    },
    /**
     * Custom event name to trigger the feedback modal
     */
    eventTriggerName: {
        type: String,
        default: "qmodii_toggle_feedback",
    },
    /**
     * Custom event name dispatched when feedback is submitted successfully
     */
    eventSubmittedName: {
        type: String,
        default: "qmodii_feedback_submitted",
    },
    /**
     * Require all option text fields to be filled out
     * @values true, false
     */
    textFieldsRequired: {
        type: Boolean,
        default: true,
    },
    /**
     * Google reCAPTCHA site key
     */
    captchaSiteKey: {
        type: String,
        default: undefined,
    },
    /**
     * The platform the feedback is being submitted from
     * @values Mobile, Web
     */
    platform: {
        type: String,
        default: undefined,
    },
    /**
     * The application name the feedback is being submitted from
     */
    application: {
        type: String,
        default: undefined,
    },
    /**
     * Open the feedback form by default on page load
     * @values true, false
     */
    openByDefault: {
        type: Boolean,
        default: false,
    },
});

const { t, locale } = useI18n();

const compName = "FeedbackForm";
registerEvents(compName);
UMS$Feedback.register(compName);

let feedbackLayout = reactive({});
let feedbackSubmitted = false;

const getInitialState = () => {
    return {
        selectedFeedbackOption: null,
        showModal: false, // Always start with modal closed until layout is loaded
        feedbackText: "",
        overallRating: null,
        feedbackOptionRatings: [],
        userConsent: false,
        isLoading: true,
        successForm: false,
        errors: {
            feedbackText: {
                msg: t("pleaseprovidefeedback"),
                error: false,
            },
            options: {
                msg: t("pleaseselectafeedbacktype"),
                error: false,
            },
            general: {
                msg: t("Error submitting feedback. Please try again."),
                error: false,
            },
        },
    };
};

const state = reactive(getInitialState());

const initState = () => {
    state.isLoading = false;
    state.selectedFeedbackOption = feedbackLayout.optionsDefaultSelection;
    state.feedbackOptionRatings = [];
    feedbackLayout.feedbackInfo.forEach((option, i) => {
        if (option.ratingScales) {
            option.ratingScales.forEach((ratingScale, j) => {
                state.feedbackOptionRatings.push({
                    ratingType: ratingScale.heading,
                    rating: null,
                });
            });
        }
        option.inputText = "";
    });

    // Show modal after layout is loaded if openByDefault is true and feedback hasn't been submitted
    if (props.openByDefault && !feedbackSubmitted) {
        state.showModal = true;
    }
};

const toggleModal = () => {
    state.showModal = !state.showModal;
};

const getLayout = async () => {
    const resp = await UMS$Feedback.getFeedbackLayout(locale.value);

    if (resp.error == "invalid token") {
        console.error("You are not authorized to use FeedbackForm");
        return;
    } else if (resp.error) {
        console.warn("No feedback layout found, falling back to default");
        feedbackLayout = locale.value === "fr" ? fallbackLayoutFr : fallbackLayoutEn;
    } else {
        feedbackLayout = resp;
    }

    initState();
};

const getOS = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let os = "Unknown";
    if (/android/i.test(userAgent)) {
        os = "Android";
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        os = "iOS";
    } else if (/Mac/.test(userAgent)) {
        os = "MacOS";
    } else if (/Windows/.test(userAgent)) {
        os = "Windows";
    } else if (/Linux/.test(userAgent)) {
        os = "Linux";
    }
    return os;
};

const getBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let browser = "Unknown";
    if (/OPR/.test(userAgent)) {
        browser = "Opera";
    } else if (/Firefox/.test(userAgent)) {
        browser = "Firefox";
    } else if (/Edg/.test(userAgent)) {
        browser = "Edge";
    } else if (/Chrome/.test(userAgent)) {
        browser = "Chrome";
    } else if (/Safari/.test(userAgent)) {
        browser = "Safari";
    }
    return browser;
};

const submitFeedback = async captchaToken => {
    const feedback = {
        pageUrl: window.location.href,
        os: getOS(),
        language: locale.value,
        feedbackText: feedbackLayout.feedbackInfo?.[state.selectedFeedbackOption]?.inputText,
        feedbackType: feedbackLayout.feedbackInfo?.[state.selectedFeedbackOption]?.heading,
        feedbackTypeRatings: state.feedbackOptionRatings.filter(rating => rating.rating != null),
        environment: getBrowser(),
        rating: state.overallRating,
        userConsent: state.userConsent,
        platform: props.platform,
        application: props.application,
    };

    const resp = await UMS$Feedback.submitFeedback(feedback, captchaToken);

    if (!resp.error) {
        state.successForm = true;
        feedbackSubmitted = true;

        // Dispatch custom event for successful feedback submission
        const submissionEvent = new CustomEvent(props.eventSubmittedName, {
            detail: {
                feedback,
                response: resp,
            },
        });
        window.dispatchEvent(submissionEvent);
    } else {
        console.log("Feedback submission failed:", resp.error);
        state.errors.general.error = true;
        state.errors.general.msg = resp.error || state.errors.general.msg;
    }
};

provide("state", state);

const onCancel = () => {
    feedbackSubmitted = true;
};

watch(
    () => state.showModal,
    () => {
        if (!state.showModal) {
            Object.assign(state, getInitialState());
            initState();
        }
    }
);

onMounted(() => {
    getLayout();
    window.addEventListener(props.eventTriggerName, toggleModal);
});

onBeforeUnmount(() => {
    window.removeEventListener(props.eventTriggerName, toggleModal);
});
</script>

<template lang="pug">
.qmodii-ui-tool.qmodii-feedbackform(:class="{ 'qmodii-loading': state.isLoading }")
    QButton.qmodii-feedback-button.qmodii-font-big(
        @click="toggleModal",
        v-if="!state.showModal && props.buttonEnabled"
    ) {{ t("Feedback") }}
    FeedbackFormModal(
        :feedbackLayout="feedbackLayout",
        :submitFeedback="submitFeedback",
        :textFieldsRequired="props.textFieldsRequired",
        :captchaSiteKey="props.captchaSiteKey",
        @close="onCancel"
    )
    FeedbackFormSuccess(:feedbackLayout="feedbackLayout")
</template>

<style lang="stylus">
:root {
    --qmodii-feedback-logo-size: 50px;
    --qmodii-feedback-gap: var(--qmodii-spacing-lg);
    --qmodii-feedback-radio-gap: var(--qmodii-spacing-md);
}

.qmodii-ui-tool.qmodii-feedbackform
    padding 0
    height 100%

    .qmodii-feedback-button
        position fixed
        top 50%
        right 0
        z-index 1000
        width 40px
        height 200px
        writing-mode vertical-lr
        text-align center
        transform translate(-50%, -50%) rotate(180deg)
        display flex
        align-items center
        justify-content center
        background-color var(--qmodii-primary)
        color white

        &:hover
            opacity 0.7
            cursor pointer
</style>
